import { useState, useEffect } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BookOpen, ArrowLeft, Calendar, User, Tag, Clock, ChevronRight } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import DOMPurify from 'dompurify';
import { blogApi } from '../lib/api';
import { HreflangTags, useSyncLanguageFromURL } from '../components/HreflangTags';
import SeoMeta from '../components/SeoMeta';
import { BLOG_SEO } from '../data/seoContent';
import PublicPageLayout from '../components/layout/PublicPageLayout';
import { extractLangFromPath, DEFAULT_LANGUAGE } from '../lib/i18nRouting';

// Detect whether a post stored its body as raw HTML or Markdown.
// Heuristic: bodies starting with an opening tag are HTML; anything else
// (including pure Markdown headings, lists, tables…) is rendered through
// react-markdown. We keep both paths so the older HTML posts in DB keep
// working without a migration script.
const looksLikeHtml = (s) => /^\s*<[a-z!]/i.test(s || '');

// Reusable public header for visitors (hidden when user is logged in)
const PublicBlogHeader = ({ backTo = '/', backLabel = 'Accueil' }) => (
  <header className="bg-[#1b2236] text-white border-b border-slate-700">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
      <div className="flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="blog-header-logo">
          <img src="/logo-landscape.png" alt="Drone Maps" className="h-12 sm:h-14 w-auto" />
        </Link>
        <nav className="flex items-center gap-4">
          <Link to={backTo} className="text-slate-300 hover:text-white transition-colors flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" />
            {backLabel}
          </Link>
          <Link to="/login" className="bg-sky-500 hover:bg-sky-600 text-white px-4 py-2 rounded-lg transition-colors">
            Connexion
          </Link>
        </nav>
      </div>
    </div>
  </header>
);

// Blog List Page
export function BlogPage() {
  useSyncLanguageFromURL();
  const { t, i18n } = useTranslation();
  const lang = i18n.language || DEFAULT_LANGUAGE;
  const seo = BLOG_SEO[lang] || BLOG_SEO.fr;
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    loadData();
    // Re-fetch when the URL language changes so `/en/blog` triggers a
    // fresh listing with English titles (and vice-versa).
     
  }, [selectedCategory, lang]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Pass current UI language so the backend overlays translations
      // for each article — fallback to FR when a translation is missing.
      const params = { lang };
      if (selectedCategory) params.category = selectedCategory;
      const [postsRes, catsRes] = await Promise.all([
        blogApi.getPosts(params),
        blogApi.getCategories()
      ]);
      setPosts(postsRes.data.posts || []);
      setCategories(catsRes.data || []);
    } catch (error) {
      console.error('Error loading blog:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    // Match the current UI language so /en/blog reads "Jul 12, 2026" etc.
    const localeMap = { fr: 'fr-FR', en: 'en-US', de: 'de-DE', it: 'it-IT', es: 'es-ES' };
    return new Date(dateStr).toLocaleDateString(localeMap[lang] || 'fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <>
      <SeoMeta
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        rawTitle
      />
      <HreflangTags />

      <PublicPageLayout
        publicHeader={<PublicBlogHeader />}
        title="Blog"
      >
        <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
          {/* Hero */}
          <section className="py-12 bg-[#1b2236] text-white">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <h1 className="text-4xl sm:text-5xl font-bold font-[Barlow] mb-4">
                {t('blog.pageTitle')}
              </h1>
              <p className="text-xl text-slate-300">
                {t('blog.pageSubtitle')}
              </p>
            </div>
          </section>

          {/* Content */}
          <main className="py-12">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Main Content */}
              <div className="flex-1">
                {loading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full mx-auto"></div>
                    <p className="text-slate-500 mt-4">{t('blog.loading')}</p>
                  </div>
                ) : posts.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
                    <p className="text-slate-500">{t('blog.empty')}</p>
                    <p className="text-slate-400 text-sm mt-2">{t('blog.emptyHint')}</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {posts.map((post) => (
                      <article key={post.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow">
                        {post.cover_image && (
                          <Link to={`/blog/${post.slug}`} className="block">
                            <div className="aspect-[16/7] w-full overflow-hidden bg-slate-100">
                              <img
                                src={post.cover_image}
                                alt={post.title}
                                loading="lazy"
                                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                                data-testid={`blog-cover-${post.slug}`}
                              />
                            </div>
                          </Link>
                        )}
                        <div className="p-6">
                          <div className="flex items-center gap-3 text-sm text-slate-500 mb-3">
                            <span className="bg-sky-100 text-sky-700 px-2 py-1 rounded-full text-xs font-medium">
                              {post.category}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {formatDate(post.created_at)}
                            </span>
                          </div>
                          <Link to={`/blog/${post.slug}`}>
                            <h2 className="text-xl font-bold text-slate-900 hover:text-sky-600 transition-colors mb-3">
                              {post.title}
                            </h2>
                          </Link>
                          <p className="text-slate-600 mb-4 line-clamp-3">
                            {post.excerpt}
                          </p>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-sm text-slate-500">
                              <User className="w-4 h-4" />
                              {post.author_name}
                            </div>
                            <Link 
                              to={`/blog/${post.slug}`}
                              className="text-sky-600 hover:text-sky-700 font-medium flex items-center gap-1"
                            >
                              {t('blog.readMore')}
                              <ChevronRight className="w-4 h-4" />
                            </Link>
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <aside className="lg:w-72">
                <div className="bg-white rounded-xl border border-slate-200 p-6 sticky top-6">
                  <h3 className="font-bold text-slate-900 mb-4">{t('blog.categoriesTitle')}</h3>
                  <div className="space-y-2">
                    <button
                      onClick={() => setSelectedCategory(null)}
                      className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                        !selectedCategory 
                          ? 'bg-sky-100 text-sky-700' 
                          : 'hover:bg-slate-100 text-slate-600'
                      }`}
                    >
                      {t('blog.allArticles')}
                    </button>
                    {categories.map((cat) => (
                      <button
                        key={cat.name}
                        onClick={() => setSelectedCategory(cat.name)}
                        className={`w-full text-left px-3 py-2 rounded-lg transition-colors flex items-center justify-between ${
                          selectedCategory === cat.name 
                            ? 'bg-sky-100 text-sky-700' 
                            : 'hover:bg-slate-100 text-slate-600'
                        }`}
                      >
                        <span>{cat.name}</span>
                        <span className="text-xs bg-slate-200 px-2 py-0.5 rounded-full">
                          {cat.count}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </main>

          {/* Footer */}
          <footer className="bg-[#1b2236] text-white py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <p className="text-slate-400">
                © {new Date().getFullYear()} Drone Maps. Tous droits réservés.
              </p>
            </div>
          </footer>
        </div>
      </PublicPageLayout>
    </>
  );
}

// Single Blog Post Page
export function BlogPostPage() {
  const { slug } = useParams();
  const location = useLocation();
  useSyncLanguageFromURL();
  const { t } = useTranslation();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Determine the requested language from URL prefix
  const urlLang = extractLangFromPath(location.pathname) || DEFAULT_LANGUAGE;

  useEffect(() => {
    loadPost();
     
  }, [slug, urlLang]);

  // Delegated to SeoMeta: title, description, meta tags, JSON-LD.
  // Kept out of the file to avoid Helmet vs SeoMeta double-mutation of head.

  const loadPost = async () => {
    setLoading(true);
    try {
      const response = await blogApi.getPost(slug, urlLang);
      setPost(response.data);
    } catch (error) {
      setError('not_found');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const localeMap = { fr: 'fr-FR', en: 'en-US', de: 'de-DE', it: 'it-IT', es: 'es-ES' };
    return new Date(dateStr).toLocaleDateString(localeMap[urlLang] || 'fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <p className="text-slate-500 mb-4">{t('blog.notFound')}</p>
        <Link to="/blog" className="text-sky-600 hover:text-sky-700">
          ← {t('common.back', 'Retour')}
        </Link>
      </div>
    );
  }

  // Enriched BlogPosting schema — includes cover image, keywords (tags),
  // language and mainEntityOfPage for proper Google Search rich results.
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.meta_description || post.excerpt,
    "image": post.cover_image ? [post.cover_image] : undefined,
    "keywords": (post.tags || []).join(', ') || undefined,
    "inLanguage": urlLang,
    "articleSection": post.category,
    "author": {
      "@type": "Person",
      "name": post.author_name
    },
    "datePublished": post.created_at,
    "dateModified": post.updated_at,
    "publisher": {
      "@type": "Organization",
      "name": "Drone Maps",
      "logo": {
        "@type": "ImageObject",
        "url": "https://drone-maps.eu/logo.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://drone-maps.eu${location.pathname}`
    }
  };
  // Strip undefined props so JSON-LD stays clean.
  Object.keys(articleSchema).forEach(k => articleSchema[k] === undefined && delete articleSchema[k]);

  const pageDescription = post.meta_description || post.excerpt || '';
  // Keywords: fall back to tags, then to the category. Truncated at ~10 items
  // to stay under Google's ~200-char soft cap.
  const pageKeywords = (post.tags && post.tags.length
    ? post.tags
    : [post.category, 'drone', 'réglementation drone', 'Drone Maps']
  ).slice(0, 10).join(', ');
  // Article-type OG image = cover image; fall back to site logo.
  const ogImage = post.cover_image || 'https://drone-maps.eu/logo.png';

  return (
    <>
      <SeoMeta
        title={post.title}
        description={pageDescription}
        keywords={pageKeywords}
        image={ogImage}
        type="article"
        jsonLd={articleSchema}
      />
      <HreflangTags />
            <PublicPageLayout
        publicHeader={<PublicBlogHeader backTo="/blog" backLabel="Blog" />}
        title={post.title}
      >
        <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
          {/* Article */}
          <article className="py-12">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Cover image — full-width hero, only rendered when set. Kept
                above meta/title so it dominates the fold. */}
            {post.cover_image && (
              <figure className="mb-8 -mx-4 sm:mx-0 rounded-none sm:rounded-xl overflow-hidden shadow-sm border border-slate-200">
                <img
                  src={post.cover_image}
                  alt={post.title}
                  className="w-full aspect-[16/7] object-cover"
                  data-testid="blog-detail-cover"
                />
              </figure>
            )}

            {/* Meta */}
            <div className="flex items-center gap-4 text-sm text-slate-500 mb-6">
              <span className="bg-sky-100 text-sky-700 px-3 py-1 rounded-full font-medium">
                {post.category}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {formatDate(post.created_at)}
              </span>
              <span className="flex items-center gap-1">
                <User className="w-4 h-4" />
                {post.author_name}
              </span>
            </div>

            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 font-[Barlow] mb-6">
              {post.title}
            </h1>

            {/* Excerpt */}
            <p className="text-xl text-slate-600 mb-8 pb-8 border-b border-slate-200">
              {post.excerpt}
            </p>

            {/* Content — render as Markdown when body is plain text,
                fall back to sanitised HTML for legacy posts already saved as HTML.
                DOMPurify strips any <script>, on* handlers and javascript: URLs
                even though only admins can post, as a defence in depth. */}
            {looksLikeHtml(post.content) ? (
              <div
                className="prose prose-lg prose-slate max-w-none prose-headings:font-[Barlow] prose-a:text-sky-600"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }}
              />
            ) : (
              <div className="prose prose-lg prose-slate max-w-none prose-headings:font-[Barlow] prose-a:text-sky-600 prose-table:my-4 prose-th:bg-slate-50 prose-th:font-semibold">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {post.content || ''}
                </ReactMarkdown>
              </div>
            )}

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="mt-8 pt-8 border-t border-slate-200">
                <div className="flex items-center gap-2 flex-wrap">
                  <Tag className="w-4 h-4 text-slate-400" />
                  {post.tags.map((tag) => (
                    <span key={tag} className="bg-slate-100 text-slate-600 px-3 py-1 rounded-full text-sm">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="mt-12 bg-gradient-to-r from-sky-500 to-blue-600 rounded-2xl p-8 text-center text-white">
              <h3 className="text-2xl font-bold font-[Barlow] mb-4">
                Besoin de vérifier votre conformité ?
              </h3>
              <p className="text-sky-100 mb-6">
                Drone Maps analyse automatiquement les zones réglementées pour vos missions drone.
              </p>
              <Link
                to="/register"
                className="inline-flex items-center gap-2 bg-white text-sky-600 px-6 py-3 rounded-lg font-semibold hover:bg-sky-50 transition-colors"
              >
                Essayer gratuitement
              </Link>
            </div>
          </div>
        </article>

          {/* Footer */}
          <footer className="bg-[#1b2236] text-white py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <p className="text-slate-400">
                © {new Date().getFullYear()} Drone Maps. Tous droits réservés.
              </p>
            </div>
          </footer>
        </div>
      </PublicPageLayout>
    </>
  );
}
